import React, { Suspense } from "react";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import lazyPage from "./components/lazyPage";
import LiveUpdateStartupReady from "./components/LiveUpdateStartupReady";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ChakraProvider, Center, Spinner, Box } from "@chakra-ui/react";
import theme from "./theme";
import { appToastOptions } from "./components/AppToast";
import { UserProvider } from "./contexts/UserContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { LanguageSync } from "./components/LanguageSwitcher";
import AnalyticsTracker from "./components/AnalyticsTracker"; // 이용 행태 분석(Clarity·PostHog) — 키 없으면 무동작. docs/ops/policy/analytics-notice-2026-09-10.md
import { useTranslation } from "react-i18next";
import ProtectedRoute from "./components/ProtectedRoute";
import PPLProtectedRoute from "./components/PPLProtectedRoute";
import ForceUpdateGate from "./components/ForceUpdateGate";
import AdminLayout from "./pages/admin/AdminLayout";
import PPLLayout from "./pages/ppl/PPLLayout";
import VideoCommerceLayout from "./pages/videoCommerce/VideoCommerceLayout";
import { captureAndLogVisit } from "./lib/attribution";
import { shortLinkRedirectTarget } from "./lib/partnerHub";
import { supabase } from "./lib/supabaseClient";
import UserLayout from "./pages/UserLayout";
import { WorkTitleProvider } from "./contexts/WorkTitleContext";
import VideoRightsLayout from "./pages/VideoRightsLayout";

// -- Pages (lazy-loaded) --
const Home = lazyPage(() => import("./pages/Home"));
const Welcome = lazyPage(() => import("./pages/Welcome")); // 「새로운 레이블리」 환영 여정(2026-09-23 · docs/ops/new-laeebly-welcome-plan.md)
const MembershipStart = lazyPage(() => import("./pages/MembershipStart")); // 「레이블리 시작하기」 — 가입비 결제 + 카드 등록 전 잠금(2026-10-20 · 시안 C)
const Landing = lazyPage(() => import("./pages/Landing"));
const MacMini = lazyPage(() => import("./pages/MacMini"));
const DemoSite = lazyPage(() => import("./pages/DemoSite")); // /demo — 외부 소개용 데모(public/demo/index.html 을 iframe 으로)
const Login = lazyPage(() => import("./pages/Login"));
const Dashboard = lazyPage(() => import("./pages/Dashboard"));
const ChannelWorkspace = lazyPage(() => import("./pages/channel/ChannelWorkspace"));
const Album = lazyPage(() => import("./pages/Album"));
const Total = lazyPage(() => import("./pages/Total"));
const Settings = lazyPage(() => import("./pages/Settings"));
const LayHistory = lazyPage(() => import("./pages/LayHistory"));
const OpsChatHistory = lazyPage(() => import("./pages/OpsChatHistory"));
const PrereleaseContracts = lazyPage(() => import("./pages/PrereleaseContracts"));
const IdentityVerify = lazyPage(() => import("./pages/IdentityVerify"));
const EditProfile = lazyPage(() => import("./pages/EditProfile"));
const ChangePassword = lazyPage(() => import("./pages/ChangePassword"));
const Withdrawal = lazyPage(() => import("./pages/Withdrawal"));
const Revenue = lazyPage(() => import("./pages/Revenue"));
const Signup = lazyPage(() => import("./pages/Signup"));
const Onboarding = lazyPage(() => import("./pages/Onboarding"));
// 구글 OAuth 콜백(2026-09-23) — 로그인 필요 · UserLayout 밖 최소 화면. docs/ops/youtube-studio-oauth-plan.md §6-4
const YoutubeCallback = lazyPage(() => import("./pages/oauth/YoutubeCallback"));
const AppRedirect = lazyPage(() => import("./pages/AppRedirect"));
const VideoRightsManager = lazyPage(() => import("./pages/video/VideoRightsManager"));
const VideoWhitelist = lazyPage(() => import("./pages/video/VideoWhitelist"));
const TotalForCopyrights = lazyPage(() => import("./pages/TotalForCopyrights"));
const PendingVerification = lazyPage(() => import("./pages/PendingVerification"));
const TermsOfService = lazyPage(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazyPage(() => import("./pages/PrivacyPolicy"));
const TermsOfServiceLegacy = lazyPage(() => import("./pages/TermsOfServiceLegacy"));
const PrivacyPolicyLegacy = lazyPage(() => import("./pages/PrivacyPolicyLegacy"));
const ResetPassword = lazyPage(() => import("./pages/ResetPassword"));
const WatchaShorts = lazyPage(() => import("./pages/WatchaShorts"));
const Notice = lazyPage(() => import("./pages/Notice"));
const Support = lazyPage(() => import("./pages/Support")); // 크리에이터 문의 창구(티켓) — docs/pages/main/Support.md
const OpenChat = lazyPage(() => import("./pages/OpenChat")); // 카카오 오픈채팅 입장 코드 — docs/pages/main/OpenChat.md
const PartnerSettlement = lazyPage(() => import("./pages/PartnerSettlement"));
const PartnerHub = lazyPage(() => import("./pages/PartnerHub")); // 파트너 허브 (시안 A·M1, 2026-09-09) — docs/pages/main/PartnerHub.md
const PartnerLeads = lazyPage(() => import("./pages/PartnerLeads")); // 권리사 영업 (영상권리사 파트너, 2026-09-11) — docs/pages/main/PartnerLeads.md
const PartnerResults = lazyPage(() => import("./pages/PartnerResults")); // 성과 — 내 건의 조회수·레이블리 매출·내 몫 (2026-09-18) — docs/pages/main/PartnerResults.md
const PartnerSources = lazyPage(() => import("./pages/PartnerSources"));
const PartnerLibrary = lazyPage(() => import("./pages/PartnerLibrary"));
const PartnerCheck = lazyPage(() => import("./pages/PartnerCheck")); // 파트너 확인 페이지(공개) — docs/pages/public/PartnerCheck.md // 자료실 — 내 코드가 박힌 파트너 마크 PNG (2026-09-22) — docs/pages/main/PartnerLibrary.md // 유입 경로 — 링크 클릭→가입→승인→첫 채널→수익 퍼널 (2026-09-18 PR B) — docs/pages/main/PartnerSources.md
const ModifyVideo = lazyPage(() => import("./pages/ModifyVideo"));
const VideoInspection = lazyPage(() => import("./pages/VideoInspection"));
const MyReadyShorts = lazyPage(() => import("./pages/MyReadyShorts")); // 완성 쇼츠 「내 완성 쇼츠」(2026-10-07)
const VideoInspectionManage = lazyPage(() => import("./pages/video/VideoInspectionManage"));
const VideoRightsSettings = lazyPage(() => import("./pages/video/VideoRightsSettings"));
const VideoRightsNotice = lazyPage(() => import("./pages/video/VideoRightsNotice"));
const VideoInsightReport = lazyPage(() => import("./pages/VideoInsightReport"));
const VideoEpisodeInsight = lazyPage(() => import("./pages/VideoEpisodeInsight"));
const VideoChannelWorks = lazyPage(() => import("./pages/VideoChannelWorks"));
const VideoShortformInsight = lazyPage(() => import("./pages/VideoShortformInsight"));
const Policies = lazyPage(() => import("./pages/Policies"));
const PolicyDetail = lazyPage(() => import("./pages/PolicyDetail"));
const MyTier = lazyPage(() => import("./pages/MyTier"));
const TierGuide = lazyPage(() => import("./pages/TierGuide")); // 등급제 안내(B안, 2026-09-07)
// 멤버십(멤버십 구독료·결제) (2026-09-07, docs/pages/main/Billing.md · docs/ops/toss-payments-impl-spec.md)
const Billing = lazyPage(() => import("./pages/Billing"));
const BillingPay = lazyPage(() => import("./pages/BillingPay"));
const BillingPass = lazyPage(() => import("./pages/BillingPass"));
const BillingResult = lazyPage(() => import("./pages/BillingResult"));
const AdminBilling = lazyPage(() => import("./pages/admin/AdminBilling"));
const AdminPromoCodes = lazyPage(() => import("./pages/admin/AdminPromoCodes"));
const Academy = lazyPage(() => import("./pages/Academy")); // 레이블리 아카데미 — 유튜브 쇼츠 8주 과정(M2·P1, 2026-09-22) docs/ops/labely-academy-plan.md
const Recommend = lazyPage(() => import("./pages/Recommend"));

// -- Dev-only preview page (2026-08-20) --
// NODE_ENV 가드를 조건식 자체에 걸어 프로덕션 빌드(webpack DefinePlugin이 NODE_ENV를 'production'으로
// 치환)에서는 이 삼항식이 상수 폴딩되어 dynamic import()·컴포넌트·라우트가 전부 죽은 코드로 제거된다.
// 로그인/권한 없이 열리는 페이지이므로 반드시 dev 전용이어야 한다 — 아래 <Route> 등록도 동일 가드 필요.
const UploadWindowPreview =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/dev/UploadWindowPreview"))
    : null;
const ChannelWorkspacePreview =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/dev/ChannelWorkspacePreview"))
    : null;
// 시청자 숏폼 온디맨드(/shorts, 구 /watch 는 아래 App() 에서 전체 페이지 리다이렉트 · 2026-09-22 대표 지시) — 독립 트리, 폐쇄형(WatchGate 가 로그인 확인) (docs/ops/viewer-shortform-ott-plan.md §4-2-3, 2026-09-09)
// 🔒 빌드 플래그 REACT_APP_WATCH_PORTAL=1 인 빌드(테스트 사이트 deploy-test.yml)와 개발 서버에서만 켠다 — 대표 지시
// 처음엔 「test-rhoonart.laeebly.io 에서만」이었고, 2026-09-10 대표 결정으로 운영 deploy.yml 에도 켰다(끄려면 그 줄만 지운다).
// ⚠ 빌드 타임 제거가 아니라 런타임 게이트다 — CRA 는 process.env 를 객체 리터럴로 넣어 terser 가 분기를 접지 못하므로
// /shorts 청크(lazy import)는 운영 build/ 에도 생성·업로드되고, 다만 절대 로드되지 않는다(2026-09-10 운영 빌드 실측). 시크릿은 없고 RPC 는 authenticated 전용.
const WATCH_PORTAL_ENABLED =
  process.env.REACT_APP_WATCH_PORTAL === "1" || process.env.NODE_ENV === "development";
const WatchRoutes = WATCH_PORTAL_ENABLED
  ? lazyPage(() => import("./pages/watch/WatchRoutes"))
  : null;
const WatchPreview =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/dev/WatchPreview"))
    : null;

// 필터 패널(시안 B) 미리보기 — 위와 같은 dev 전용 규칙. 라우트 등록도 동일 가드.
const WorkFilterPreview =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/dev/WorkFilterPreview"))
    : null;
// 작품 추천 미리보기(가상 데이터) — 같은 dev 전용 규칙.
const RecommendPreview =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/dev/RecommendPreview"))
    : null;

// 같은 가드 — 신청 동의·승인 알림 체험 화면. 어드민 라우트(/admin/work-consent-preview)와
// 같은 컴포넌트를 쓰되, 이 dev 경로는 로그인 없이 열려 디자인 확인용으로만 쓴다.
const WorkConsentPreviewDev =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/admin/AdminWorkConsentPreview"))
    : null;

// -- Admin pages (lazy-loaded) --
const AdminDashboard = lazyPage(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazyPage(() => import("./pages/admin/AdminUsers"));
const AdminSignupTypes = lazyPage(() => import("./pages/admin/AdminSignupTypes")); // 가입 유형 — docs/pages/admin/AdminSignupTypes.md
const AdminCollabChannels = lazyPage(() => import("./pages/admin/AdminCollabChannels")); // 협업 채널 — docs/pages/admin/AdminCollabChannels.md
const AdminChannels = lazyPage(() => import("./pages/admin/AdminChannels"));
const AdminMusic = lazyPage(() => import("./pages/admin/AdminMusic"));
const AdminVideos = lazyPage(() => import("./pages/admin/AdminVideos"));
const AdminVideoApplications = lazyPage(() => import("./pages/admin/AdminVideoApplications"));
const AdminInspections = lazyPage(() => import("./pages/admin/AdminInspections"));
const AdminInspectionCompliance = lazyPage(() => import("./pages/admin/AdminInspectionCompliance"));
const AdminReadyShorts = lazyPage(() => import("./pages/admin/AdminReadyShorts")); // 완성 쇼츠 관리(2026-10-07 · docs/pages/admin/AdminReadyShorts.md)
const AdminSupport = lazyPage(() => import("./pages/admin/AdminSupport"));
const AdminVideoRevenue = lazyPage(() => import("./pages/admin/AdminVideoRevenue"));
const AdminRevenueSimulator = lazyPage(() => import("./pages/admin/AdminRevenueSimulatorPage"));
const AdminBrandInquiries = lazyPage(() => import("./pages/admin/AdminBrandInquiries"));
const AdminInquiries = lazyPage(() => import("./pages/admin/AdminInquiries")); // 크리에이터 문의 관리 — docs/pages/admin/AdminInquiries.md
const AdminLay = lazyPage(() => import("./pages/admin/AdminLay")); // 부가기능 › 레이 AI 도우미 — 시키기·승인·답변 사전·확인할 답변·받는 사람 묶음 — docs/pages/admin/AdminLay.md
const AdminLaeeblyGuide = lazyPage(() => import("./pages/admin/AdminLaeeblyGuide")); // docs.laeebly.io 원고 편집 (부가기능)
const AdminCalendar = lazyPage(() => import("./pages/admin/AdminCalendar")); // 레이블리 캘린더 — 예약 페이지·시간표·구글 연동 (부가기능, 2026-09-10)
const AdminContentTranslation = lazyPage(() => import("./pages/admin/AdminContentTranslation")); // 공지·FAQ 번역 관리 (부가기능)
const AdminDesignCenter = lazyPage(() => import("./pages/admin/AdminDesignCenter")); // 디자인센터 — 채널 종류 심볼 원본·결정 기록 (부가기능, 2026-10-20)
const AdminLinks = lazyPage(() => import("./pages/admin/AdminLinks")); // 대외 링크 모음 (부가기능)
const AdminOpenChat = lazyPage(() => import("./pages/admin/AdminOpenChat")); // 오픈채팅 입장 확인 (부가기능) — docs/pages/admin/AdminOpenChat.md
const AdminShowroom = lazyPage(() => import("./pages/admin/AdminShowroom")); // 제작 사례 쇼룸 — 사례 넣기·영상 올리기 (부가기능, 2026-10-27) — docs/pages/admin/AdminShowroom.md
const Showroom = lazyPage(() => import("./pages/showroom/Showroom")); // 권리사 미팅 화면 — 어드민 레이아웃 밖 전면 (2026-10-27)
const AdminScheduledTasks = lazyPage(() => import("./pages/admin/AdminScheduledTasks")); // 예정 작업 — 날짜가 되면 할 일 (부가기능)
const AdminWhatsNew = lazyPage(() => import("./pages/admin/AdminWhatsNew")); // 새 소식 — 운영팀 새 기능 안내 원장 (2026-09-14, 시안 C)
const AdminYoutubeOAuth = lazyPage(() => import("./pages/admin/AdminYoutubeOAuth")); // 유튜브 연결 — 구글 OAuth grant 현황·원장·스위치 (부가기능, 2026-09-23) — docs/pages/admin/AdminYoutubeOAuth.md
const AdminLessonStats = lazyPage(() => import("./pages/admin/AdminLessonStats")); // 레슨 이수·오답 통계 (읽기 전용)
const AdminPartners = lazyPage(() => import("./pages/admin/AdminPartners")); // 파트너 관리 — docs/pages/admin/AdminPartners.md
const AdminKakaoRevenue = lazyPage(() => import("./pages/admin/AdminKakaoRevenue"));
const AdminNaverRevenue = lazyPage(() => import("./pages/admin/AdminNaverRevenue"));
const AdminSettlement = lazyPage(() => import("./pages/admin/AdminSettlement"));
const AdminSettlementRequest = lazyPage(() => import("./pages/admin/AdminSettlementRequest"));
const AdminShortsMusicRevenue = lazyPage(() => import("./pages/admin/AdminShortsMusicRevenue"));
const AdminVideoSettlement = lazyPage(() => import("./pages/admin/AdminVideoSettlement"));
const AdminPublicMusicSettlement = lazyPage(() => import("./pages/admin/AdminPublicMusicSettlement"));
const AdminPromotionSettlement = lazyPage(() => import("./pages/admin/AdminPromotionSettlement"));
const AdminPartnerSettlement = lazyPage(() => import("./pages/admin/AdminPartnerSettlement"));
const AdminKakaoNaverVideoSettlement = lazyPage(() => import("./pages/admin/AdminKakaoNaverVideoSettlement"));
const AdminUserSettlementView = lazyPage(() => import("./pages/admin/AdminUserSettlementView"));
const AdminPopup = lazyPage(() => import("./pages/admin/AdminPopup"));
const AdminAppVersion = lazyPage(() => import("./pages/admin/AdminAppVersion"));
const AdminContentMusic = lazyPage(() => import("./pages/admin/AdminContentMusic"));
const AdminChannelLicensedVideo = lazyPage(() => import("./pages/admin/AdminChannelLicensedVideo"));
const AdminYouTubeInsights = lazyPage(() => import("./pages/admin/AdminYouTubeInsights"));
const AdminChannelMonitoring = lazyPage(() => import("./pages/admin/AdminChannelMonitoring"));
const AdminRecommendationMonitor = lazyPage(() => import("./pages/admin/AdminRecommendationMonitor"));
// /shorts 입장 코드 발급·기록 (2026-09-10, docs/ops/watch-guest-code-plan.md §7)
const AdminWatchCodes = lazyPage(() => import("./pages/admin/AdminWatchCodes"));
const AdminWatchCodeDetail = lazyPage(() => import("./pages/admin/AdminWatchCodes").then((m) => ({ default: m.AdminWatchCodeDetail })));
const AdminRecommendationChannel = lazyPage(() => import("./pages/admin/AdminRecommendationChannel"));
const AdminEmbargoMonitor = lazyPage(() => import("./pages/admin/AdminEmbargoMonitor"));
const AdminPrerelease = lazyPage(() => import("./pages/admin/AdminPrerelease"));
// 본방 전 소재 체험판 — 테스트 사이트 빌드(deploy-test.yml REACT_APP_PRERELEASE_SANDBOX=1)에서만 라우트가 생긴다
const PRERELEASE_SANDBOX = process.env.REACT_APP_PRERELEASE_SANDBOX === "1";
const PrereleaseSandbox = lazyPage(() => import("./pages/admin/PrereleaseSandbox")); // 운영 빌드에도 청크는 생기지만 라우트가 없고 백엔드 파일도 올라가지 않는다
const AdminInspectionPolicy = lazyPage(() => import("./pages/admin/AdminInspectionPolicy")); // 작품 검수 정책 일괄 입력 (검수·지오블락 PR-B)
const AdminShoppingCollaboration = lazyPage(() => import("./pages/admin/AdminShoppingCollaboration"));
const AdminVideoSettlementDashboard = lazyPage(() => import("./pages/admin/AdminVideoSettlementDashboard"));
const AdminNotifications = lazyPage(() => import("./pages/admin/AdminNotifications"));
const AdminAnnouncements = lazyPage(() => import("./pages/admin/AdminAnnouncements"));
const AdminAnnouncementEdit = lazyPage(() => import("./pages/admin/AdminAnnouncementEdit"));
const AdminAnnouncementDelivery = lazyPage(() => import("./pages/admin/AdminAnnouncementDelivery"));
const AdminPlatformNews = lazyPage(() => import("./pages/admin/AdminPlatformNews"));
const AdminPlatformNewsEdit = lazyPage(() => import("./pages/admin/AdminPlatformNewsEdit"));
const AdminPromotions = lazyPage(() => import("./pages/admin/AdminPromotions"));
const AdminPromotionEditor = lazyPage(() => import("./pages/admin/AdminPromotionEditor"));
const AdminPromotionDetail = lazyPage(() => import("./pages/admin/AdminPromotionDetail"));
const AdminMusicPromotionEditor = lazyPage(() => import("./pages/admin/AdminMusicPromotionEditor"));
const AdminMusicPromotionDetail = lazyPage(() => import("./pages/admin/AdminMusicPromotionDetail"));
const AdminPolicyConsents = lazyPage(() => import("./pages/admin/AdminPolicyConsents"));
const AdminWorkConsentPreview = lazyPage(() => import("./pages/admin/AdminWorkConsentPreview"));
const AdminCreatorTiers = lazyPage(() => import("./pages/admin/AdminCreatorTiers"));
const AdminTierBonus = lazyPage(() => import("./pages/admin/AdminTierBonus")); // 등급 가점 (2026-09-04)
const AdminTierAdjustments = lazyPage(() => import("./pages/admin/AdminTierAdjustments")); // 가감점 관리 (2026-10-21)

// -- User promotion pages (lazy-loaded) --
const Promotions = lazyPage(() => import("./pages/Promotions"));
const PromotionDetail = lazyPage(() => import("./pages/PromotionDetail"));
const PromotionSubmit = lazyPage(() => import("./pages/PromotionSubmit"));
const MyPromotionApplications = lazyPage(() => import("./pages/MyPromotionApplications"));
const MusicPromotionDetail = lazyPage(() => import("./pages/MusicPromotionDetail"));
const MusicPromotionSubmit = lazyPage(() => import("./pages/MusicPromotionSubmit"));

// -- PPL pages (lazy-loaded) --
const PPLHome = lazyPage(() => import("./pages/ppl/PPLHome"));
const PPLWorksExplore = lazyPage(() => import("./pages/ppl/PPLWorksExplore"));
const PPLWorkDetail = lazyPage(() => import("./pages/ppl/PPLWorkDetail"));
const BroadcasterWorksManage = lazyPage(() => import("./pages/ppl/BroadcasterWorksManage"));
const BroadcasterPPLManage = lazyPage(() => import("./pages/ppl/BroadcasterPPLManage"));
const AdminCategoryManage = lazyPage(() => import("./pages/ppl/AdminCategoryManage"));

// -- Video Commerce pages (lazy-loaded) --
const VideoCommerceHome = lazyPage(() => import("./pages/videoCommerce/VideoCommerceHome"));
const VideoCommerceRegister = lazyPage(() => import("./pages/videoCommerce/VideoCommerceRegister"));
const VideoCommerceResults = lazyPage(() => import("./pages/videoCommerce/VideoCommerceResults"));

// -- Shopping Tag pages (lazy-loaded) --
const PplShoppingTagHelper = lazyPage(() => import("./pages/shoppingTag/PplShoppingTagHelper"));
const PplShoppingTagDetail = lazyPage(() => import("./pages/shoppingTag/PplShoppingTagDetail"));

// -- Platform portal pages (lazy-loaded, role="Platform" 전용) --
const PlatformLayout = lazyPage(() => import("./pages/platform/PlatformLayout"));
const PlatformKakaoDashboard = lazyPage(() => import("./pages/platform/PlatformKakaoDashboard"));
const PlatformKakaoChannels = lazyPage(() => import("./pages/platform/PlatformKakaoChannels"));

const PageLoader = () => (
  <Center h="100vh">
    <Spinner size="xl" color="brand.500" thickness="4px" />
  </Center>
);

/**
 * 언어 경계 — 언어가 바뀌면 `key` 가 바뀌어 아래 화면 전체가 다시 마운트된다.
 * 문구 대부분은 훅이 아닌 `tt()`(src/i18n/tt.js)로 그려지므로, 이 재마운트가
 * 언어 전환을 화면에 반영하는 장치다. 언어 전환은 드문 동작이라 재조회 비용은 감수한다.
 */
function LanguageBoundary({ children }) {
  const { i18n } = useTranslation();
  return <React.Fragment key={i18n.language}>{children}</React.Fragment>;
}

function App() {
  // 파트너 유입 파라미터(?ref=…&utm_*) 는 어느 경로로 들어와도 여기서 한 번 읽어 30일 보관한다 (src/lib/attribution.js)
  React.useEffect(() => {
    // 파트너 링크 방문은 첫 유입 저장 때 한 번 partner_link_visit_log 로 (2026-09-18, 유입 경로 화면보다 먼저 클릭을 모은다)
    try { captureAndLogVisit(window.location, document.referrer, (args) => supabase.rpc("partner_link_visit_log", args)); } catch (e) { /* 보관 실패는 무시 */ }
  }, []);
  // 채널 워크스페이스 미리보기는 fixture만 사용한다. 전역 User/Notification provider와
  // ForceUpdateGate도 Supabase/Auth를 초기화할 수 있으므로 이 dev 경로는 독립 트리로 렌더한다.
  // production에서는 NODE_ENV 상수 폴딩으로 분기·lazy import·route가 모두 제거된다.
  const isChannelWorkspacePreview =
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/dev/channel-workspace-preview");

  // 시청자 서비스 /shorts/* — 폐쇄형(로그인 후 열람, WatchGate). UserProvider·NotificationProvider·ForceUpdateGate 를 타지 않는 독립 트리로
  // 그린다(UserContext 의 PUBLIC_PATHS 정확 일치 검사와 handleAuthError 세션 삭제를 피한다 — 🔴 보호 파일을 건드리지 않음).
  // dev 미리보기 /dev/watch-preview 는 같은 화면을 픽스처 api 로 그린다.
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  // 구 주소 /watch… → /shorts… (2026-09-22 대표 지시 「laeebly.io/watch → laeebly.io/shorts」). 이미 보낸 초대문·링크(/watch/enter 등)가 끊기지 않게
  // 같은 경로를 /shorts 밑으로 전체 페이지 이동시킨다(라우터 navigate 금지 — 독립 트리). 훅(useEffect) 뒤에 두어야 하고, /watcha-shorts(UserContext.PUBLIC_PATHS) 는
  // 다른 화면이라 `(\/|$)` 로 구분한다. 내부 이름(WatchRoutes·watchPaths·watch_guest_* RPC·laeebly_watch_* 키)은 그대로 — 바뀐 것은 공개 주소 접두뿐.
  if (typeof window !== "undefined" && /^\/watch(\/|$)/.test(pathname)) {
    window.location.replace(`/shorts${pathname.slice(6)}${window.location.search}${window.location.hash}`);
    return null;
  }
  // 파트너 짧은 링크 /go/코드[/플랫폼] → /signup?ref=…&utm_medium=shortlink (2026-09-28 대표 결정 ④ · 시안 3-A, src/lib/partnerHub.js).
  // /shorts 와 같은 이유로 라우터·UserProvider 를 타지 않고 전체 페이지 이동한다(UserContext PUBLIC_PATHS 정확 일치 검사 — 🔴 보호 파일 무변경).
  // 유입 보관·방문 기록은 /signup 마운트의 captureAndLogVisit 이 한다(같은 코드 24시간 안 중복 기록 없음).
  if (typeof window !== "undefined") {
    const shortTarget = shortLinkRedirectTarget(pathname, window.location.search);
    if (shortTarget) {
      window.location.replace(shortTarget);
      return null;
    }
  }
  const isWatchPortal =
    WATCH_PORTAL_ENABLED && WatchRoutes && (pathname === "/shorts" || pathname.startsWith("/shorts/"));
  const isWatchPreview =
    process.env.NODE_ENV === "development" && pathname.startsWith("/dev/watch-preview");

  if (isWatchPortal || (isWatchPreview && WatchPreview)) {
    return (
      <ChakraProvider theme={theme} toastOptions={appToastOptions}>
        <Box minH="100vh" bg="neutral.50">
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ChunkErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                {isWatchPortal ? <WatchRoutes /> : <WatchPreview />}
              </Suspense>
            </ChunkErrorBoundary>
          </Router>
        </Box>
      </ChakraProvider>
    );
  }

  if (isChannelWorkspacePreview && ChannelWorkspacePreview) {
    return (
      <ChakraProvider theme={theme} toastOptions={appToastOptions}>
        <Box minH="100vh" bg="neutral.50">
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ChunkErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route
                    path="/dev/channel-workspace-preview"
                    element={
                      <Navigate
                        to="/dev/channel-workspace-preview/preview-alpha"
                        replace
                      />
                    }
                  />
                  <Route
                    path="/dev/channel-workspace-preview/:channelId/*"
                    element={<ChannelWorkspacePreview />}
                  />
                </Routes>
              </Suspense>
            </ChunkErrorBoundary>
          </Router>
        </Box>
      </ChakraProvider>
    );
  }

  return (
    <ChakraProvider theme={theme} toastOptions={appToastOptions}>
      <Box minH="100vh" bg="neutral.50">
        <ForceUpdateGate />
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <UserProvider>
          {/* 로그인 계정에 저장된 언어를 화면에 적용한다 (docs/ops/app-i18n-plan.md §3) */}
          <LanguageSync />
          <AnalyticsTracker />
          <NotificationProvider>
            {/* 작품명은 공식 현지 제목이 있을 때만 옮긴다 — 언어당 한 번 읽어 카드들이 나눠 쓴다 (§19) */}
            <WorkTitleProvider>
            <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            <LanguageBoundary>
            <LiveUpdateStartupReady />
            <Routes>
              {/* Public pages */}
              {/* '/' 는 비로그인 공개 소개 랜딩 — 로그인 상태면 /home, 네이티브 앱은 /login (Landing 내부 분기) */}
              <Route path="/" element={<Landing />} />
              {/* 새로운 레이블리 — 인트로 + 한 달 개편 + 동의. UserLayout 밖(전면). WelcomeRedirect(UserLayout)가 보낸다 */}
              <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
              {/* 레이블리 시작하기 — 정식 등급이 되는 순간 가입비 결제 + 카드 등록 전 잠금. UserLayout 밖(전면). SignupLockRedirect(UserLayout)가 보낸다 */}
              <Route path="/start" element={<ProtectedRoute><MembershipStart /></ProtectedRoute>} />
              <Route path="/login" element={<Login />} />
              {/* Apple 제품 페이지 스타일 디자인 데모 — 인증/레이아웃 없이 단독 렌더 */}
              <Route path="/mac-mini" element={<MacMini />} />
              <Route path="/demo" element={<DemoSite />} />
              {/* 권리사 미팅용 제작 사례 쇼룸 — 어드민만, 어드민 메뉴가 미팅 상대에게 보이지 않게 레이아웃 밖 전면 (docs/pages/admin/AdminShowroom.md) */}
              <Route path="/showroom" element={<ProtectedRoute requireAdmin={true}><Showroom /></ProtectedRoute>} />
              <Route path="/showroom/:caseKey" element={<ProtectedRoute requireAdmin={true}><Showroom /></ProtectedRoute>} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/pending-verification" element={<PendingVerification />} />
              {/* 로그인 직후 시작 준비(첫 채널·스튜디오·앱) — UserLayout 없이 전체 화면 */}
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/oauth/youtube/callback" element={<ProtectedRoute><YoutubeCallback /></ProtectedRoute>} />
              {/* 앱 스토어 이동 — QR·가입 메일이 가리키는 공개 주소 (로그인 불필요) */}
              <Route path="/app" element={<AppRedirect />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              {/* 파트너 확인 페이지 — 명함·서명 마크의 QR·링크가 온다. 비로그인 (2026-09-22, UserContext.PUBLIC_PATHS 에도 있음) */}
              <Route path="/partner-check" element={<PartnerCheck />} />
              {/* 이전 버전 보존 — 약관 제2조 「현행 약관과 함께」 공지 + 시행 뒤 「이전 약관 보기」 */}
              <Route path="/terms-of-service/2025" element={<TermsOfServiceLegacy />} />
              <Route path="/privacy-policy/2026-08" element={<PrivacyPolicyLegacy />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/withdrawal" element={<Withdrawal />} />
              {/* Dev-only: 분기별 미리보기 (로그인/권한 불필요, NODE_ENV==='development' 일 때만 등록) */}
              {process.env.NODE_ENV === "development" && UploadWindowPreview && (
                <Route path="/dev/upload-window-preview" element={<UploadWindowPreview />} />
              )}
              {process.env.NODE_ENV === "development" && WorkFilterPreview && (
                <Route path="/dev/work-filter-panel" element={<WorkFilterPreview />} />
              )}
              {process.env.NODE_ENV === "development" && RecommendPreview && (
                <Route path="/dev/recommend-preview" element={<RecommendPreview />} />
              )}
              {process.env.NODE_ENV === "development" && WorkConsentPreviewDev && (
                <Route path="/dev/work-consent-preview" element={<WorkConsentPreviewDev />} />
              )}

              {/* User pages — wrapped in UserLayout. 파트너 홈은 '/home' ('/' 는 공개 랜딩) */}
              <Route element={<ProtectedRoute><UserLayout /></ProtectedRoute>}>
                <Route path="home" element={<Home />} />
                {/* 채널 탭 = 채널 작업 공간. /channel 은 첫 채널로 이동하거나(채널 없음) 등록 안내를 띄운다 (2026-09-02) */}
                <Route path="channel" element={<ChannelWorkspace />} />
                <Route path="channel/:channelId/*" element={<ChannelWorkspace />} />
                <Route path="album" element={<Album />} />
                <Route path="revenue" element={<Revenue />} />
                <Route path="my-tier" element={<MyTier />} />
                {/* 등급제 안내 — 「자세히 보기」의 목적지 (B안, 2026-09-07) — docs/pages/main/TierGuide.md */}
                <Route path="tier-guide" element={<TierGuide />} />
                {/* 멤버십(멤버십 구독료·결제) — docs/pages/main/Billing.md. success/fail 은 토스 리다이렉트 복귀 */}
                <Route path="billing" element={<Billing />} />
                <Route path="billing/pass" element={<BillingPass />} />
                <Route path="billing/pay" element={<BillingPay />} />
                <Route path="billing/success" element={<BillingResult />} />
                <Route path="billing/fail" element={<BillingResult />} />
                {/* 레이블리 아카데미 — 크리에이터만, 스위치(app_config.academy_enabled) 꺼짐이면 페이지가 /home 으로 보낸다 — docs/pages/main/Academy.md */}
                <Route path="academy" element={<Academy />} />
                {/* 작품 추천 (1단계 · 규칙 기반) — docs/pages/main/Recommend.md */}
                <Route path="recommend" element={<Recommend />} />
                <Route path="total" element={<Total />} />
                <Route path="settings" element={<Settings />} />
                {/* 레이 「내 대화 기록」 — 시간순·질문별·받은 편지함, 보기 방식은 사용자 선택 (2026-09-09) — docs/pages/main/LayHistory.md */}
                <Route path="settings/lay/history" element={<LayHistory />} />
                {/* 운영팀 대화 기록(읽기 전용) — 2026-10-23 · docs/ops/ops-chat-room-plan.md */}
                <Route path="settings/ops-chats" element={<OpsChatHistory />} />
                <Route path="settings/ops-chats/:roomId" element={<OpsChatHistory />} />
                {/* 본방 전 소재 「내 계약서」 (2026-09-23) — docs/ops/prerelease-source-pledge-plan.md §12 */}
                <Route path="settings/prerelease-contracts" element={<PrereleaseContracts />} />
                {/* 기존 회원 본인확인(토스인증·NHN KCP) — 스위치 꺼짐이면 「준비 중」 (2026-09-28) — docs/ops/identity-toss-kcp-readiness.md */}
                <Route path="settings/identity" element={<IdentityVerify />} />
                {/* 파트너 포털의 「내 대화 기록」 — 같은 화면, 파트너 허용 경로(/partner/*) 안 (대표 지시 2026-09-10) */}
                <Route path="partner/lay/history" element={<LayHistory />} />
                {/* 약관·정책 — 숏폼 정책 전문은 대외 비공개라 보호 라우트로만 연다 (동의 기획 §3-1) */}
                <Route path="policies" element={<Policies />} />
                <Route path="policies/:key" element={<PolicyDetail />} />
                <Route path="edit-profile" element={<EditProfile />} />
                <Route path="change-password" element={<ChangePassword />} />
                <Route path="notice/:id" element={<Notice />} />
                <Route path="notice" element={<Notice />} />
                {/* 문의하기(티켓) — 목록·새 문의 / 스레드. 알림은 DB 트리거가 넣는다 (docs/pages/main/Support.md) */}
                <Route path="support" element={<Support />} />
                <Route path="support/:id" element={<Support />} />
                {/* 오픈채팅 입장 코드 — 「[공지방] 레이블리 - 유튜브」 오픈채팅 이전 · 11/1 0시부터(서버 판정) · docs/pages/main/OpenChat.md */}
                <Route path="openchat" element={<OpenChat />} />
                <Route path="watcha-shorts" element={<WatchaShorts />} />
                <Route path="partner-settlement" element={<PartnerSettlement />} />
                {/* 파트너 허브 — 계약 서명 · 수료 튜토리얼 · 코드·링크 (Partner 역할 — 「파트너로 승인」은 Creator, Partner). 정산은 /partner-settlement 그대로 */}
                <Route path="partner" element={<PartnerHub />} />
                {/* 권리사 영업 — 영상권리사 파트너 전용 화면 (2026-09-11). :tab 보다 앞에 둔다 */}
                <Route path="partner/leads" element={<PartnerLeads />} />
                <Route path="partner/results" element={<PartnerResults />} />
                <Route path="partner/sources" element={<PartnerSources />} />
                <Route path="partner/library" element={<PartnerLibrary />} />
                <Route path="partner/:tab" element={<PartnerHub />} />
                <Route path="partner/docs/:slug" element={<PartnerHub />} />
                <Route path="modify-video" element={<ModifyVideo />} />
                <Route path="shopping-tag-helper" element={<PplShoppingTagHelper />} />
                <Route path="shopping-tag-helper/detail" element={<PplShoppingTagDetail />} />
                <Route path="video-inspection" element={<VideoInspection />} />
                <Route path="ready-shorts" element={<MyReadyShorts />} />
                <Route path="promotions" element={<Promotions />} />
                <Route path="promotions/my" element={<MyPromotionApplications />} />
                {/* 2026-10-26 유료 프로모션 별도 메뉴 — 운영팀이 등록한 내 채널의 유료 프로모션·영상 제출 */}
                <Route path="paid-promotions" element={<MyPromotionApplications mode="paid" />} />
                <Route path="promotions/music/:id" element={<MusicPromotionDetail />} />
                <Route path="promotions/music/:id/submit" element={<MusicPromotionSubmit />} />
                <Route path="promotions/:id" element={<PromotionDetail />} />
                <Route path="promotions/:id/submit" element={<PromotionSubmit />} />
              </Route>

              {/* Video Rights pages — wrapped in VideoRightsLayout */}
              <Route element={<ProtectedRoute><VideoRightsLayout /></ProtectedRoute>}>
                <Route path="video-dashboard" element={<Dashboard />} />
                <Route path="video-insight" element={<VideoInsightReport />} />
                <Route path="video-episode-insight" element={<VideoEpisodeInsight />} />
                <Route path="video-channel-works" element={<VideoChannelWorks />} />
                <Route path="video-shortform-insight" element={<VideoShortformInsight />} />
                <Route path="total-copyrights" element={<TotalForCopyrights />} />
                <Route path="video/rights-manager" element={<VideoRightsManager />} />
                <Route path="video/whitelist" element={<VideoWhitelist />} />
                <Route path="video/inspection-manage" element={<VideoInspectionManage />} />
                <Route path="video/settings" element={<VideoRightsSettings />} />
                <Route path="video/edit-profile" element={<EditProfile />} />
                <Route path="video/change-password" element={<ChangePassword />} />
                <Route path="video/notice" element={<VideoRightsNotice />} />
                <Route path="video/notice/:id" element={<VideoRightsNotice />} />
              </Route>

              {/* Admin pages */}
              <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="signup-types" element={<AdminSignupTypes />} />
                <Route path="channels" element={<AdminChannels />} />
                <Route path="collab-channels" element={<AdminCollabChannels />} />
                <Route path="music" element={<AdminMusic />} />
                <Route path="content-music" element={<AdminContentMusic />} />
                <Route path="videos" element={<AdminVideos />} />
                <Route path="video-applications" element={<AdminVideoApplications />} />
                <Route path="inspections" element={<AdminInspections />} />
                {/* 작품별 검수 정책·지오블락 나라 목록 일괄 입력 — 대표 입력용 (docs/pages/admin/AdminInspectionPolicy.md) */}
                <Route path="inspection-policy" element={<AdminInspectionPolicy />} />
                <Route path="inspection-compliance" element={<AdminInspectionCompliance />} />
                <Route path="ready-shorts" element={<AdminReadyShorts />} />
                <Route path="embargo" element={<AdminEmbargoMonitor />} />
                {/* 본방 전 소재 계약 — 승인·원장·작품 조건·사고·스위치 (2026-09-23, docs/pages/admin/AdminPrerelease.md) */}
                <Route path="prerelease" element={<AdminPrerelease />} />
                {PRERELEASE_SANDBOX && <Route path="prerelease-sandbox" element={<PrereleaseSandbox />} />}
                <Route path="revenue-simulator" element={<AdminRevenueSimulator />} />
                <Route path="brand-inquiries" element={<AdminBrandInquiries />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                {/* 2026-10-23 「문의」 › 「대화방 관리」 탭 — docs/ops/ops-chat-room-plan.md */}
                <Route path="inquiries/chats" element={<AdminInquiries />} />
                <Route path="inquiries/chats/:roomId" element={<AdminInquiries />} />
                <Route path="inquiries/guide" element={<AdminInquiries />} />
                <Route path="inquiries/:id" element={<AdminInquiries />} />
                <Route path="lay" element={<AdminLay />} />
                <Route path="lay/:tab" element={<AdminLay />} />
                <Route path="laeebly-guide" element={<AdminLaeeblyGuide />} />
                <Route path="calendar" element={<AdminCalendar />} />
                <Route path="calendar/:key" element={<AdminCalendar />} />
                <Route path="content-translation" element={<AdminContentTranslation />} />
                <Route path="design-center" element={<AdminDesignCenter />} />
                <Route path="links" element={<AdminLinks />} />
                <Route path="openchat" element={<AdminOpenChat />} />
                <Route path="showroom" element={<AdminShowroom />} />
                <Route path="scheduled-tasks" element={<AdminScheduledTasks />} />
                <Route path="whats-new" element={<AdminWhatsNew />} />
                <Route path="youtube-oauth" element={<AdminYoutubeOAuth />} />
                <Route path="video-revenue" element={<AdminVideoRevenue />} />
                <Route path="kakao-revenue" element={<AdminKakaoRevenue />} />
                <Route path="naver-revenue" element={<AdminNaverRevenue />} />
                <Route path="support" element={<AdminSupport />} />
                <Route path="settlement" element={<AdminSettlement />} />
                <Route path="public-music-settlement" element={<AdminPublicMusicSettlement />} />
                <Route path="promotion-settlement" element={<AdminPromotionSettlement />} />
                <Route path="partner-settlement" element={<AdminPartnerSettlement />} />
                <Route path="settlement-request" element={<AdminSettlementRequest />} />
                <Route path="shorts-music-revenue" element={<AdminShortsMusicRevenue />} />
                <Route path="video-settlement" element={<AdminVideoSettlement />} />
                <Route path="video-settlement-dashboard" element={<AdminVideoSettlementDashboard />} />
                <Route path="kakao-naver-video-settlement" element={<AdminKakaoNaverVideoSettlement />} />
                <Route path="user-settlement-view" element={<AdminUserSettlementView />} />
                <Route path="channel-licensed-video" element={<AdminChannelLicensedVideo />} />
                <Route path="channel-monitoring" element={<AdminChannelMonitoring />} />
                <Route path="recommendation" element={<AdminRecommendationMonitor />} />
                <Route path="recommendation/:channelId" element={<AdminRecommendationChannel />} />
                <Route path="watch-codes" element={<AdminWatchCodes />} />
                <Route path="watch-codes/:codeId" element={<AdminWatchCodeDetail />} />
                <Route path="popup" element={<AdminPopup />} />
                <Route path="app-version" element={<AdminAppVersion />} />
                <Route path="youtube-insights" element={<AdminYouTubeInsights />} />
                <Route path="shopping-collaboration" element={<AdminShoppingCollaboration />} />
                <Route path="notifications" element={<AdminNotifications />} />
                <Route path="announcements" element={<AdminAnnouncements />} />
                <Route path="announcements/new" element={<AdminAnnouncementEdit />} />
                <Route path="announcements/:id" element={<AdminAnnouncementEdit />} />
                <Route path="announcements/:id/delivery" element={<AdminAnnouncementDelivery />} />
                {/* 플랫폼 소식 — 유튜브·네이버·카카오·인스타그램·틱톡 변화 (기획 docs/ops/platform-news-plan.md) */}
                <Route path="platform-news" element={<AdminPlatformNews />} />
                <Route path="platform-news/new" element={<AdminPlatformNewsEdit />} />
                <Route path="platform-news/:id" element={<AdminPlatformNewsEdit />} />
                <Route path="policy-consents" element={<AdminPolicyConsents />} />
                {/* 체험용 — 아무것도 저장하지 않는다. 어드민 전용이라 크리에이터에게 노출되지 않음 */}
                <Route path="work-consent-preview" element={<AdminWorkConsentPreview />} />
                {/* 레슨 이수·오답 통계 — work_lesson_completion 읽기만 (기획 first-apply-lesson-plan §10) */}
                <Route path="lesson-stats" element={<AdminLessonStats />} />
                {/* 파트너 원장(유형·비율·요구 버전·재수료 기한·해지) + 유입 집계 (2026-09-09) */}
                <Route path="partners" element={<AdminPartners />} />
                {/* 구 경로 — 채널·계정 화면을 「숏폼 정책 관리」 한 곳으로 합치면서 이동
                    (채널 기준 → 계정 기준 2026-08-31, 메뉴 통합 2026-09-01) */}
                <Route path="creator-eligibility" element={<Navigate to="/admin/policy-consents" replace />} />
                <Route path="channel-eligibility" element={<Navigate to="/admin/policy-consents" replace />} />
                <Route path="creator-tiers" element={<AdminCreatorTiers />} />
                <Route path="tier-bonus" element={<AdminTierBonus />} />
                <Route path="tier-adjustments" element={<AdminTierAdjustments />} />
                {/* 멤버십 어드민 — docs/pages/admin/AdminBilling.md · AdminPromoCodes.md */}
                <Route path="billing" element={<AdminBilling />} />
                <Route path="promo-codes" element={<AdminPromoCodes />} />
                <Route path="promotions" element={<AdminPromotions />} />
                <Route path="promotions/new" element={<AdminPromotionEditor />} />
                <Route path="promotions/music/new" element={<AdminMusicPromotionEditor />} />
                <Route path="promotions/music/:id" element={<AdminMusicPromotionDetail />} />
                <Route path="promotions/music/:id/edit" element={<AdminMusicPromotionEditor />} />
                <Route path="promotions/:id" element={<AdminPromotionDetail />} />
                <Route path="promotions/:id/edit" element={<AdminPromotionEditor />} />
              </Route>

              {/* PPL portal */}
              <Route path="/ppl" element={<PPLProtectedRoute><PPLLayout /></PPLProtectedRoute>}>
                <Route index element={<PPLHome />} />
                <Route path="works" element={<PPLWorksExplore />} />
                <Route path="works/:id" element={<PPLWorkDetail />} />
                <Route
                  path="broadcaster/works"
                  element={<PPLProtectedRoute requireKT={true}><BroadcasterWorksManage /></PPLProtectedRoute>}
                />
                <Route
                  path="broadcaster/ppl"
                  element={<PPLProtectedRoute requireKT={true}><BroadcasterPPLManage /></PPLProtectedRoute>}
                />
                <Route
                  path="admin/categories"
                  element={<PPLProtectedRoute requireAdmin={true}><AdminCategoryManage /></PPLProtectedRoute>}
                />
              </Route>

              {/* Video Commerce portal */}
              <Route path="/video-analysis" element={<ProtectedRoute><VideoCommerceLayout /></ProtectedRoute>}>
                <Route index element={<VideoCommerceHome />} />
                <Route path="register" element={<VideoCommerceRegister />} />
                <Route path="results" element={<VideoCommerceResults />} />
              </Route>

              {/* Platform portal — 플랫폼사(role="Platform") 전용, ProtectedRoute Platform 분기가 접근 제어 */}
              <Route path="/platform" element={<ProtectedRoute><PlatformLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/platform/kakao-dashboard" replace />} />
                <Route path="kakao-dashboard" element={<PlatformKakaoDashboard />} />
                <Route path="kakao-channels" element={<PlatformKakaoChannels />} />
                {/* 구 일별 신규 경로 — 대시보드로 통합됨 */}
                <Route path="kakao-daily" element={<Navigate to="/platform/kakao-dashboard" replace />} />
              </Route>
            </Routes>
            </LanguageBoundary>
            </Suspense>
            </ChunkErrorBoundary>
            </WorkTitleProvider>
          </NotificationProvider>
          </UserProvider>
        </Router>
      </Box>
    </ChakraProvider>
  );
}

export default App;
